import { Fragment, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import api, { formatApiError } from "../../../lib/api";
import { exportRowsToXlsx } from "../../../lib/exportToXlsx";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../../components/ui/dialog";

export const STATUS = {
  draft: ["Draft", "bg-slate-100 text-slate-600 border-slate-200"],
  submitted: ["Pending Approval", "bg-amber-50 text-amber-800 border-amber-200"],
  processing: ["Processing", "bg-amber-50 text-amber-800 border-amber-200"],
  approved: ["Approved", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  rejected: ["Rejected", "bg-rose-50 text-rose-700 border-rose-200"],
  cancellation_requested: ["Cancellation Requested", "bg-orange-50 text-orange-700 border-orange-200"],
  cancelled: ["Cancelled", "bg-slate-100 text-slate-500 border-slate-200"],
  reversed: ["Reversed", "bg-slate-100 text-slate-500 border-slate-200"],
  not_ready: ["Not Ready", "bg-slate-100 text-slate-600 border-slate-200"],
  pending_approval: ["Pending Approval", "bg-amber-50 text-amber-800 border-amber-200"],
  ready: ["Ready for Payroll", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  exception: ["Exception — Review Required", "bg-rose-50 text-rose-700 border-rose-200"],
  finalised: ["Payroll Finalised", "bg-slate-900 text-white border-slate-900"],
  reopened: ["Reopened for Correction", "bg-blue-50 text-blue-700 border-blue-200"],
};

export const Badge = ({ s, testId }) => {
  const [label, cls] = STATUS[s] || [s || "—", "bg-slate-100 text-slate-600 border-slate-200"];
  return <span className={`inline-flex items-center whitespace-nowrap text-[11px] font-semibold px-2 py-0.5 border rounded-full ${cls}`} data-testid={testId}>{label}</span>;
};

export const fmtHalf = (v) => {
  if (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const neg = n < 0 ? "−" : "";
  const a = Math.abs(n);
  const whole = Math.floor(a + 1e-9);
  const halfPart = a - whole >= 0.25 ? "½" : "";
  return `${neg}${whole || !halfPart ? whole : ""}${halfPart}`;
};
export const days = (v) => (v === null || v === undefined || v === "" ? "—" : `${fmtHalf(v)} d`);
export const mins = (m) => `${Math.floor((m || 0) / 60)}h ${String((m || 0) % 60).padStart(2, "0")}m`;
export const TXN = {
  opening: "Opening balance", opening_adjustment: "Opening adjustment", accrual: "Monthly accrual", leave_debit: "Leave approved",
  compoff_setoff: "Comp-Off set-off", reversal: "Reversal", manual_adjustment: "Manual adjustment", lop_conversion: "Unpaid in payroll (balance restored)",
  lop_reversal: "Payroll reopened", earned: "Comp-Off earned", utilised: "Comp-Off used (day off)", setoff: "Set-off against leave",
  expired: "Expired", adjustment_credit: "Adjustment (+)", adjustment_debit: "Adjustment (−)",
};

export function Stat({ label, value, sub, tone = "", testId }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-sm p-5 ${tone}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="font-serif text-3xl mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export function XlsxButton({ columns, rows, filename, testId = "export-xlsx" }) {
  return (
    <Button variant="outline" size="sm" disabled={!rows?.length} data-testid={testId}
      onClick={() => exportRowsToXlsx({ filename, sheetName: "Report", columns: columns.map((c) => ({ key: c.key, label: c.label })), rows })}>
      <Download className="h-4 w-4 mr-1" /> Excel
    </Button>
  );
}

export function DataTable({ columns, rows, empty = "No records", testId, rowTestId, onRow, render = {} }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>{columns.map((c) => <th key={c.key} className="text-left font-medium px-3 py-2 whitespace-nowrap">{c.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {!rows?.length && <tr><td colSpan={columns.length} className="p-8 text-center text-slate-400">{empty}</td></tr>}
          {rows?.map((r, i) => (
            <tr key={r.id || i} className={onRow ? "cursor-pointer hover:bg-slate-50" : ""} onClick={() => onRow?.(r)} data-testid={rowTestId ? `${rowTestId}-${r.id || i}` : undefined}>
              {columns.map((c) => <td key={c.key} className="px-3 py-2 align-top">{render[c.key] ? render[c.key](r[c.key], r) : (r[c.key] === true ? "Yes" : r[c.key] === false ? "" : r[c.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LedgerTable({ rows, testId = "ledger-table", unit = "d" }) {
  const columns = [{ key: "effective_date", label: "Date" }, { key: "txn_type", label: "Transaction" }, { key: "app_no", label: "Reference" },
    { key: "days", label: "Days" }, { key: "running_balance", label: "Balance" }, { key: "approved_by", label: "By" }, { key: "remarks", label: "Remarks" }, { key: "id", label: "Txn ID" }].filter((c) => c.key !== "running_balance" || rows?.some((r) => r.running_balance !== undefined));
  return (
    <DataTable testId={testId} columns={columns} rows={rows} empty="No ledger transactions yet"
      render={{ txn_type: (v) => TXN[v] || v, days: (v) => <span className={v < 0 ? "text-rose-700" : "text-emerald-700"}>{v > 0 ? "+" : ""}{fmtHalf(v)}{unit}</span>,
        running_balance: (v) => <b>{fmtHalf(v)}</b>, id: (v) => <span className="text-[10px] text-slate-400">{v}</span> }} />
  );
}

// Generic decision dialog used by Partner approvals.
export function DecideDialog({ item, onClose, onDone }) {
  const [remarks, setRemarks] = useState("");
  const [amount, setAmount] = useState("");
  const [exception, setException] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!item) return null;
  const go = async (action) => {
    setBusy(true);
    try {
      await api.post(item.url, { action, remarks, exception, approved_days: amount === "" ? null : Number(amount) });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="decide-dialog">
        <DialogHeader><DialogTitle>{item.title}</DialogTitle><DialogDescription>Review the details, then approve or reject. Opening this request does not approve it.</DialogDescription></DialogHeader>
        <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm bg-slate-50 border border-slate-200 p-4 rounded-sm" data-testid="decide-details">
          {item.details.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (<Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="col-span-2 font-medium break-words">{v}</dd></Fragment>))}
        </dl>
        {item.warning && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 p-3 rounded-sm" data-testid="decide-warning">{item.warning}</div>}
        {item.amountLabel && (
          <div><Label className="eyebrow">{item.amountLabel}</Label><Input type="number" step="0.5" min="0.5" placeholder={String(item.amountDefault)} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-2 rounded-sm" data-testid="decide-amount" /></div>
        )}
        <div><Label className="eyebrow">Remarks {item.warning ? "(required)" : "(required to reject)"}</Label><Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-2 rounded-sm" data-testid="decide-remarks" /></div>
        {item.allowException && (
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={exception} onCheckedChange={(v) => setException(!!v)} data-testid="decide-exception" /> Approve as an authorised exception (Partner only)</label>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => go("reject")} disabled={busy} className="text-rose-700" data-testid="decide-reject-btn">Reject</Button>
          <Button onClick={() => go("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="decide-approve-btn">Approve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReasonDialog({ title, description, onClose, onSubmit, confirm = "Submit", testId = "reason-dialog" }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
        <Textarea rows={3} placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reason-input" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Back</Button><Button disabled={reason.trim().length < 3} onClick={() => onSubmit(reason)} data-testid="reason-submit-btn">{confirm}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
